import { useMemo } from 'react';

import { useListWebhookDeliveriesQuery } from '@/features/webhooks/webhooksApi';
import {
  useAdminHealthControllerSnapshotQuery,
  useIntegrationsControllerPendingCountsQuery,
} from '@/services/generated/api';
import type { NavGroup, NavChild, NavItem } from './types';

/**
 * The three menu counters §4.12 of the screen inventory asks for:
 * «Recepción», «Conexiones», and «Entregas fallidas».
 *
 * ## Why here and not in the `<feature>.nav.ts` files
 *
 * All three feature files noted it and none could resolve it, for the same
 * reason: a `<feature>.nav.ts` is a **module-level constant**, and a count comes
 * from a hook. It is not a typing problem — `NavChild.badge` has been declared
 * since #150 — but one of where a subscription to a query can live.
 * So the tree with counters is built **on top of** the static tree, in a hook,
 * and `AppLayout` consumes it. The static tree remains the source of the
 * structure; this only sticks numbers on it.
 *
 * ## The three counters, and what each measures
 *
 * - **«Recepción»** — `inboundPending` from `GET /api/admin/health`: DTEs received
 *   and not acknowledged. Each has 8 calendar days before the SII takes it as
 *   tacitly accepted, so it is the counter with a real consequence.
 * - **«Conexiones»** — `GET /api/integrations/connections/pending-counts`:
 *   documents under review plus unresolved mappings, aggregated over every
 *   connection. The endpoint was added for this (C-05): the ones that already
 *   existed are per connection, and building the total from the frontend would
 *   have been one fetch per active connection on every render of the shell.
 * - **«Entregas fallidas»** — `GET /api/webhooks/deliveries?status=dead`.
 *
 * ## Two decisions worth not reverting without reading this
 *
 * **1. A zero paints NO badge.** `badge: 0` would render a "0" next to
 * «Recepción», which is noise: the badge exists to draw attention to something
 * that has to be done, and "there is nothing to do" is better communicated by
 * the badge's absence. That is why the counters travel as `undefined` when they
 * are zero — and while the query is in flight too, so the menu does not flicker
 * from "no badge" to "3" on the first render of every screen.
 *
 * **2. The deliveries one SATURATES, and that has to be known.**
 * `GET /deliveries` returns an array with a `limit`, not a `COUNT`, so the real
 * number is "at least this many". `limit: BADGE_CAP + 1` is requested and up to
 * `BADGE_CAP` is shown: beyond that, the badge says `BADGE_CAP` rather than
 * lying with an exact number it is not. The distinction matters little in
 * practice — whoever has 50 dead deliveries does not need to know whether it is
 * 50 or 300 to go and look — but it matters that the number is not presented as
 * exact when it is not.
 */

/**
 * The deliveries badge's cap. It is not a visual design decision: it is how far
 * counting can go without a `COUNT` endpoint.
 */
export const BADGE_CAP = 50;

/** A count is shown only if it is greater than zero. */
function badgeFor(count: number | undefined): number | undefined {
  if (count === undefined || count <= 0) return undefined;
  return count;
}

export interface NavBadges {
  /** DTEs recibidos sin acusar. */
  '/inbound'?: number;
  /** Documents under review + unresolved mappings, across every connection. */
  '/integrations/connections'?: number;
  /** Dead webhook deliveries («Entregas fallidas»). */
  '/webhooks/deliveries'?: number;
}

/**
 * The counts, keyed by the route of the destination that shows them.
 *
 * It is indexed by `to` and not by label because `to` is what identifies a
 * destination in the tree (`router.test.tsx` already demands it not repeat); a
 * label can be rewritten without breaking anything and this map would break in
 * silencio.
 */
export function useNavBadges(enabled: boolean): NavBadges {
  const health = useAdminHealthControllerSnapshotQuery(undefined, { skip: !enabled });
  const integrations = useIntegrationsControllerPendingCountsQuery(undefined, { skip: !enabled });
  const deliveries = useListWebhookDeliveriesQuery(
    { status: 'dead', limit: BADGE_CAP + 1 },
    { skip: !enabled },
  );

  return useMemo(() => {
    const integrationsCount =
      integrations.data === undefined
        ? undefined
        : integrations.data.documentsNeedingReview + integrations.data.unresolvedMappings;

    const deadCount =
      deliveries.data === undefined ? undefined : Math.min(deliveries.data.length, BADGE_CAP);

    return {
      '/inbound': badgeFor(health.data?.inboundPending),
      '/integrations/connections': badgeFor(integrationsCount),
      '/webhooks/deliveries': badgeFor(deadCount),
    };
  }, [health.data, integrations.data, deliveries.data]);
}

/**
 * Sticks the counters onto the already-grouped tree.
 *
 * It walks parents and children because the three destinations are not at the
 * same level: «Recepción» is first level, «Conexiones» and «Entregas fallidas» are children.
 *
 * It returns the SAME reference when there is no counter to apply, so
 * `AppShell` — which has effects depending on `nav`'s identity — does not run
 * them again over an equivalent but new tree.
 */
export function withNavBadges(
  groups: readonly NavGroup[],
  badges: NavBadges,
): readonly NavGroup[] {
  const badgeOf = (to: string): number | undefined =>
    (badges as Readonly<Record<string, number | undefined>>)[to];

  let changed = false;

  const next = groups.map((group) => {
    const items = group.items.map((item): NavItem => {
      const own = badgeOf(item.to);
      const children = item.children?.map((child): NavChild => {
        const childBadge = badgeOf(child.to);
        if (childBadge === undefined) return child;
        changed = true;
        return { ...child, badge: childBadge };
      });
      const childrenChanged = children !== undefined && children.some((c, i) => c !== item.children?.[i]);
      if (own === undefined && !childrenChanged) return item;
      if (own !== undefined) changed = true;
      return {
        ...item,
        ...(own === undefined ? {} : { badge: own }),
        ...(children === undefined ? {} : { children }),
      };
    });
    return items.some((item, i) => item !== group.items[i]) ? { ...group, items } : group;
  });

  return changed ? next : groups;
}
